// WHAT: Card component displaying daily MiracleFlow session summary with morning and evening session data.
// WHY:  Provides visual representation of daily session data including scribing answers, reading notes, and evening reflection for history review.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @tracq/frontend-shared/utils, @/components/ui, ../../hooks/useDailySessionSummaries, lucide-react
// RISKS:
//   - NOTE: Data structure assumes specific input types (question_1, question_2, question_3, text); may fail with unexpected formats.
//   - WARN: Input filtering uses hardcoded step names ('scribing', 'evening_reflection', 'reading'); changes may break display.
//   - PERF: Multiple array filtering operations may impact performance with many inputs.

import { Sun, Moon, CheckCircle } from "lucide-react";
import React from "react";

import { Card } from "@/components/ui";
import { cn } from "@/shared/utils";

import {
  type DailySessionSummary,
  type SessionInputSummary,
} from "../../hooks/useDailySessionSummaries";

interface DailySessionCardProperties {
  className?: string;
  dailySummary: DailySessionSummary;
}

/**
 * Finds input by step type and input type.
 *
 * @param inputs - Array of session inputs
 *
 * @param step - Step name (e.g., 'scribing', 'evening_reflection', 'reading')
 *
 * @param inputType - Input type (e.g., 'question_1', 'question_2', 'text')
 *
 * @returns Matching input or undefined if not found
 *
 */
const getInputByType = (
  inputs: SessionInputSummary[],
  step: string,
  inputType: string,
): SessionInputSummary | undefined => {
  return inputs.find((input) => input.step === step && input.input_type === inputType);
};

/**
 * WHAT:
 *   Extracts scribing answers (3 questions) from morning session inputs.
 *
 * WHY:
 *   Provides structured access to morning scribing question answers.
 *
 * INPUTS:
 *   - inputs: SessionInputSummary[] - Morning session inputs array
 *
 * OUTPUT:
 *   - { question1, question2, question3 } - Object with question answers or undefined
 *
 * RISKS:
 *   - WARN: Assumes input types are 'question_1', 'question_2', 'question_3'; may fail with different structure.
 *
 * @param inputs - Morning session inputs array
 *
 * @returns Object with question answers or undefined
 *
 */
const getScribingAnswers = (
  inputs: SessionInputSummary[],
): {
  question1?: SessionInputSummary;
  question2?: SessionInputSummary;
  question3?: SessionInputSummary;
} => {
  const question1 = getInputByType(inputs, "scribing", "question_1");
  const question2 = getInputByType(inputs, "scribing", "question_2");
  const question3 = getInputByType(inputs, "scribing", "question_3");

  const result: {
    question1?: SessionInputSummary;
    question2?: SessionInputSummary;
    question3?: SessionInputSummary;
  } = {};
  if (question1 !== undefined) {
    result.question1 = question1;
  }
  if (question2 !== undefined) {
    result.question2 = question2;
  }
  if (question3 !== undefined) {
    result.question3 = question3;
  }
  return result;
};

/**
 * WHAT:
 *   Extracts evening reflection answers (3 questions) from evening session inputs.
 *
 * WHY:
 *   Provides structured access to evening reflection question answers.
 *
 * INPUTS:
 *   - inputs: SessionInputSummary[] - Evening session inputs array
 *
 * OUTPUT:
 *   - { question1, question2, question3 } - Object with question answers or undefined
 *
 * RISKS:
 *   - WARN: Assumes input types are 'question_1', 'question_2', 'question_3'; may fail with different structure.
 *
 * @param inputs - Evening session inputs array
 *
 * @returns Object with question answers or undefined
 *
 */
const getEveningReflectionAnswers = (
  inputs: SessionInputSummary[],
): {
  question1?: SessionInputSummary;
  question2?: SessionInputSummary;
  question3?: SessionInputSummary;
} => {
  const question1 = getInputByType(inputs, "evening_reflection", "question_1");
  const question2 = getInputByType(inputs, "evening_reflection", "question_2");
  const question3 = getInputByType(inputs, "evening_reflection", "question_3");

  const result: {
    question1?: SessionInputSummary;
    question2?: SessionInputSummary;
    question3?: SessionInputSummary;
  } = {};
  if (question1 !== undefined) {
    result.question1 = question1;
  }
  if (question2 !== undefined) {
    result.question2 = question2;
  }
  if (question3 !== undefined) {
    result.question3 = question3;
  }
  return result;
};

/**
 * WHAT:
 *   Extracts reading notes text from morning session inputs.
 *
 * WHY:
 *   Provides access to reading step text input.
 *
 * INPUTS:
 *   - inputs: SessionInputSummary[] - Morning session inputs array
 *
 * OUTPUT:
 *   - SessionInputSummary | undefined - Reading input or undefined if not found
 *
 * RISKS:
 *   - NOTE: Assumes input type is 'text' for reading step; may fail with different structure.
 *
 * @param inputs - Morning session inputs array
 *
 * @returns Reading input or undefined if not found
 *
 */
const getReadingNotes = (inputs: SessionInputSummary[]): SessionInputSummary | undefined => {
  return getInputByType(inputs, "reading", "text");
};

/**
 * WHAT:
 *   Card component displaying daily MiracleFlow session summary.
 *
 * WHY:
 *   Provides users with visual representation of daily session data including morning scribing answers,
 *   reading notes, and evening reflection answers. Enables review of past session content for
 *   reflection and progress tracking.
 *
 * PROPS:
 *   - dailySummary: DailySessionSummary - Daily summary object containing morning and evening session data
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays date header with completion indicator if both sessions completed
 *   - Shows morning session section with scribing answers (3 questions) and reading notes
 *   - Displays evening session section with reflection answers (3 questions)
 *   - Filters inputs by step type and input type to extract specific data
 *   - Shows empty state if no session data is available
 *   - Uses sticky header for better scrolling experience
 *
 * RISKS:
 *   - NOTE: Data structure assumes specific input types (question_1, question_2, question_3, text); may fail with unexpected formats.
 *   - WARN: Input filtering uses hardcoded step names ('scribing', 'evening_reflection', 'reading'); changes may break display.
 *   - PERF: Multiple array filtering operations may impact performance with many inputs.
 *   - NOTE: Question labels are hardcoded in German; may not match user preferences.
 *
 * @param root0
 *
 * @param root0.dailySummary
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const DailySessionCard: React.FC<DailySessionCardProperties> = ({
  className,
  dailySummary,
}) => {
  // NOTE: Safe access to properties with fallbacks for null/undefined handling
  const hasBothSessions = dailySummary.has_both_sessions;
  const dateDisplay = dailySummary.date_display;
  const morningSession = dailySummary.morning_session;
  const eveningSession = dailySummary.evening_session;

  return (
    <Card
      className={cn(
        "bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card max-h-96 overflow-y-auto",
        className,
      )}
      padding="md"
      variant="default"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sticky top-0 bg-[#1E293B] backdrop-blur-sm py-2 -mx-2 px-2 border-b border-[#334155]">
        <div className="flex items-center gap-2 flex-1">
          {hasBothSessions ? (
            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500/20 border border-green-500/30">
              <CheckCircle className="w-3 h-3 text-green-400" />
            </div>
          ) : null}
          <span className="text-lg font-semibold text-[#F1F5F9]">{dateDisplay}</span>
        </div>
        <div className="flex items-center gap-2">
          {morningSession ? (
            <div className="flex items-center justify-center w-6 h-6 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue">
              <Sun className="w-3 h-3 text-white" />
            </div>
          ) : null}
          {eveningSession ? (
            <div className="flex items-center justify-center w-6 h-6 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange">
              <Moon className="w-3 h-3 text-white" />
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {/* Morning Session */}
        {morningSession ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue">
                <Sun className="w-3 h-3 text-white" />
              </div>
              <h4 className="font-semibold text-[#F1F5F9]">Morning Session</h4>
            </div>

            {/* Scribing Answers */}
            {(() => {
              const scribing = getScribingAnswers(morningSession.inputs);
              if (scribing.question1 || scribing.question2 || scribing.question3) {
                return (
                  <div className="bg-[#0F172A] rounded-tracq p-3 space-y-2 border border-[#334155]">
                    <h5 className="text-sm font-medium text-[#CBD5E1]">Scribing</h5>
                    {scribing.question1 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Dankbar für:</span>
                        <p className="text-[#F1F5F9] mt-1">{scribing.question1.value}</p>
                      </div>
                    ) : null}
                    {scribing.question2 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Guter Tag:</span>
                        <p className="text-[#F1F5F9] mt-1">{scribing.question2.value}</p>
                      </div>
                    ) : null}
                    {scribing.question3 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Selbstbekräftigung:</span>
                        <p className="text-[#F1F5F9] mt-1">{scribing.question3.value}</p>
                      </div>
                    ) : null}
                  </div>
                );
              }
              return null;
            })()}

            {/* Reading Notes */}
            {(() => {
              const reading = getReadingNotes(morningSession.inputs);
              if (reading) {
                return (
                  <div className="bg-[#0F172A] rounded-tracq p-3 border border-[#334155]">
                    <h5 className="text-sm font-medium text-[#CBD5E1] mb-2">Reading Notes</h5>
                    <p className="text-[#F1F5F9] text-xs">{reading.value}</p>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        ) : null}

        {/* Evening Session */}
        {eveningSession ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange">
                <Moon className="w-3 h-3 text-white" />
              </div>
              <h4 className="font-semibold text-[#F1F5F9]">Evening Session</h4>
            </div>

            {/* Evening Reflection Answers */}
            {(() => {
              const reflection = getEveningReflectionAnswers(eveningSession.inputs);
              if (reflection.question1 || reflection.question2 || reflection.question3) {
                return (
                  <div className="bg-[#0F172A] rounded-tracq p-3 space-y-2 border border-[#334155]">
                    <h5 className="text-sm font-medium text-[#CBD5E1]">Evening Reflection</h5>
                    {reflection.question1 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Gutes getan:</span>
                        <p className="text-[#F1F5F9] mt-1">{reflection.question1.value}</p>
                      </div>
                    ) : null}
                    {reflection.question2 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Gelernt:</span>
                        <p className="text-[#F1F5F9] mt-1">{reflection.question2.value}</p>
                      </div>
                    ) : null}
                    {reflection.question3 ? (
                      <div className="text-xs">
                        <span className="text-[#94A3B8]">Tolle Dinge:</span>
                        <p className="text-[#F1F5F9] mt-1">{reflection.question3.value}</p>
                      </div>
                    ) : null}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        ) : null}

        {/* Empty State */}
        {!morningSession && !eveningSession && (
          <div className="text-center py-4">
            <p className="text-[#94A3B8] text-sm">Keine Session-Daten verfügbar</p>
          </div>
        )}
      </div>
    </Card>
  );
};
